import { useEffect, useSyncExternalStore } from 'react';

// Detail pages (customer/supplier order) show a contextual header title such as
// "Zamówienie do dostawcy: ZD/20260603/P0WZU" instead of the route-keyed menu
// title. The number is only known after the page fetches its data, so Layout
// can't derive it from the pathname — pages publish it here instead. Only one
// override can be active at a time (routes render one page), and React runs the
// old page's cleanup before the new page's effect, so mount/unmount ordering is
// safe.
let currentOverride: string | null = null;
const listeners = new Set<() => void>();

const setOverride = (title: string | null) => {
  if (currentOverride === title) return;
  currentOverride = title;
  listeners.forEach((listener) => listener());
};

/**
 * Publish a header title override while the calling page is mounted.
 * Pass null while data is still loading — the header keeps the default title.
 */
export const usePageHeaderTitleOverride = (title: string | null) => {
  useEffect(() => {
    setOverride(title);
    return () => setOverride(null);
  }, [title]);
};

/** Layout-side subscription to the current override (null = use default). */
export const useCurrentPageHeaderTitleOverride = (): string | null =>
  useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => listeners.delete(onStoreChange);
    },
    () => currentOverride,
  );
